import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useSettings } from '@/contexts/SettingsContext';
import { Car, AlertTriangle, FileText, Wrench, BarChart2, Plus, Trash2, Edit } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

const CarViewPage = () => {
  const { carId } = useParams<{ carId: string }>();
  const { cars, deleteCar } = useCar();
  const { currentCar } = useCarSelectionContext();
  const { t } = useSettings();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const selectedCar = carId 
    ? cars.find(c => c.id === carId) 
    : currentCar;
  
  if (!selectedCar) {
    return (
      <PageLayout 
        title="Car Not Found" 
        showBackButton 
        onBack={() => navigate('/cars')}
      >
        <div className="flex flex-col items-center justify-center h-[60vh] text-center">
          <Car size={64} className="text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold mb-2">Car Not Found</h2>
          <p className="text-gray-500 mb-6">The car you're looking for doesn't exist or was deleted</p>
          <Button onClick={() => navigate('/cars')} className="bg-primary">
            View All Cars
          </Button>
        </div>
      </PageLayout>
    );
  }
  
  const openIssues = selectedCar.issues?.filter(issue => issue.status !== 'resolved') || [];
  const recentIssues = [...(selectedCar.issues || [])].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, 3);
  
  const recentRepairs = [...(selectedCar.repairs || [])].sort((a, b) => 
    new Date(b.date).getTime() - new Date(a.date).getTime()
  ).slice(0, 3);
  
  const getHealthStatus = () => {
    if (!openIssues.length) {
      return { 
        icon: '✅', 
        message: 'All good', 
        description: 'No open issues',
        color: 'bg-lime/20 text-black'
      };
    }
    
    const urgentIssues = openIssues.filter(issue => 
      issue.aiSummary?.includes('urgent') || 
      issue.title.toLowerCase().includes('urgent') ||
      issue.description.toLowerCase().includes('urgent')
    );
    
    if (urgentIssues.length > 1) {
      return { 
        icon: '🚨', 
        message: `${urgentIssues.length} urgent issues`, 
        description: 'Needs immediate attention',
        color: 'bg-red-500/20 text-red-700'
      };
    }
    
    if (urgentIssues.length === 1) {
      return { 
        icon: '⚠️', 
        message: 'Urgent issue', 
        description: urgentIssues[0].title,
        color: 'bg-yellow-400/20 text-black'
      };
    }
    
    const recurringTitles = new Set();
    const recurring = openIssues.filter(issue => {
      if (recurringTitles.has(issue.title)) {
        return true;
      }
      recurringTitles.add(issue.title);
      return false;
    });
    
    if (recurring.length) {
      return { 
        icon: '🔁', 
        message: 'Recurring issue not resolved', 
        description: recurring[0].title,
        color: 'bg-yellow-400/20 text-black'
      };
    }
    
    return { 
      icon: '⚠️', 
      message: `${openIssues.length} open issue${openIssues.length > 1 ? 's' : ''}`, 
      description: openIssues[0].title,
      color: 'bg-yellow-400/20 text-black'
    };
  };
  
  const healthStatus = getHealthStatus();
  
  const handleDelete = () => {
    deleteCar(selectedCar.id);
    toast({
      title: "Car Deleted",
      description: `${selectedCar.brand} ${selectedCar.model} has been removed`,
    });
    navigate('/cars');
  };
  
  const handleEdit = () => {
    navigate(`/cars/edit/${selectedCar?.id}`);
  };
  
  return (
    <PageLayout 
      title={selectedCar?.nickname || (selectedCar?.brand + ' ' + selectedCar?.model)} 
      showBackButton 
      onBack={() => navigate('/cars')}
    >
      <div className="space-y-6">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex justify-between items-start">
              <div>
                {selectedCar?.nickname ? (
                  <>
                    <CardTitle className="text-2xl">{selectedCar.nickname}</CardTitle>
                    <CardDescription>{selectedCar?.brand} {selectedCar?.model}</CardDescription>
                  </>
                ) : (
                  <CardTitle className="text-2xl">{selectedCar?.brand} {selectedCar?.model}</CardTitle>
                )}
                <CardDescription>{selectedCar?.year}</CardDescription>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={handleEdit}
                  className="h-9 w-9"
                >
                  <Edit size={18} />
                </Button>
                <Avatar className="h-16 w-16 bg-gray-100">
                  <AvatarFallback className="text-gray-400 bg-gray-100">
                    <Car size={32} />
                  </AvatarFallback>
                </Avatar>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {selectedCar.fuelType && (
                <div>
                  <p className="text-sm text-gray-500">Fuel Type</p>
                  <p className="font-medium">{selectedCar.fuelType}</p>
                </div>
              )}
              {selectedCar.transmission && (
                <div>
                  <p className="text-sm text-gray-500">Transmission</p>
                  <p className="font-medium">{selectedCar.transmission}</p>
                </div>
              )}
              {selectedCar.vin && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-500">VIN</p>
                  <p className="font-medium font-mono text-sm">{selectedCar.vin}</p>
                </div>
              )}
              {selectedCar.licensePlate && (
                <div>
                  <p className="text-sm text-gray-500">License Plate</p>
                  <p className="font-medium">{selectedCar.licensePlate}</p>
                </div>
              )}
              {selectedCar.mileage && (
                <div>
                  <p className="text-sm text-gray-500">Mileage</p>
                  <p className="font-medium">{selectedCar.mileage.toLocaleString()} km</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="md:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle>Health Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className={`rounded-lg p-3 ${healthStatus.color} flex-1`}>
                  <div className="text-lg font-semibold flex items-center">
                    <span className="mr-2">{healthStatus.icon}</span>
                    {healthStatus.message}
                  </div>
                  <p className="text-sm mt-1">{healthStatus.description}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                onClick={() => navigate('/log-symptom')}
                className="w-full bg-lime text-black hover:bg-lime/90 flex items-center justify-center"
              >
                <AlertTriangle size={18} className="mr-2" />
                Log Symptom
              </Button>
              
              <Button 
                onClick={() => navigate(`/log-repair/${selectedCar.id}`)}
                className="w-full bg-soft-blue text-white hover:bg-soft-blue/90 flex items-center justify-center"
              >
                <Wrench size={18} className="mr-2" />
                Log Repair
              </Button>
            </CardContent>
          </Card>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate('/log-symptom')}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <AlertTriangle size={20} className="mr-2 text-lime" />
                Log New Symptom
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>Record a new issue or symptom with your car</CardDescription>
            </CardContent>
          </Card>
          
          <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate(`/log-repair/${selectedCar.id}`)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <Wrench size={20} className="mr-2 text-soft-blue" />
                Log New Repair
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>Record a repair or maintenance service</CardDescription>
            </CardContent>
          </Card>
          
          <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate(`/cars/${selectedCar.id}`)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <FileText size={20} className="mr-2 text-soft-blue" />
                View All Symptoms
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>See what issues have been logged for this car</CardDescription>
            </CardContent>
          </Card>
          
          <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate(`/reports/health/${selectedCar?.id || ''}`)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <BarChart2 size={20} className="mr-2 text-soft-blue" />
                Generate Health Report
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>Get a shareable PDF report of your car's health</CardDescription>
            </CardContent>
          </Card>
          
          <Card className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer" onClick={() => navigate(`/service-request/generate/${selectedCar?.id || ''}`)}>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center text-lg">
                <FileText size={20} className="mr-2 text-soft-blue" />
                Generate Service Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <CardDescription>Create a customizable service request for your mechanic</CardDescription>
            </CardContent>
          </Card>
        </div>
        
        {recentIssues.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Recent Issues</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentIssues.map(issue => (
                <div 
                  key={issue.id}
                  onClick={() => navigate(`/symptom-summary/${selectedCar.id}/${issue.id}`)}
                  className="p-3 border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                >
                  <div className="flex items-start">
                    <AlertTriangle 
                      size={20} 
                      className={
                        issue.status === 'resolved' 
                          ? 'text-lime' 
                          : issue.status === 'partially_resolved'
                          ? 'text-orange-300'
                          : issue.status === 'processing' 
                          ? 'text-yellow-400' 
                          : 'text-red-500'
                      } 
                    />
                    <div className="ml-3 flex-1">
                      <h3 className="font-medium">{issue.title}</h3>
                      <p className="text-sm text-gray-500 line-clamp-1">{issue.description}</p>
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-400">
                          {new Date(issue.date).toLocaleDateString()}
                        </span>
                        <Badge variant={
                          issue.status === 'resolved' 
                            ? 'outline' 
                            : issue.status === 'partially_resolved'
                            ? 'secondary'
                            : issue.status === 'processing' 
                            ? 'secondary'
                            : 'default'
                        }>
                          {issue.status === 'resolved' 
                            ? 'Resolved' 
                            : issue.status === 'partially_resolved'
                            ? 'Partially Resolved'
                            : issue.status === 'processing' 
                            ? 'In Progress' 
                            : 'New'}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {selectedCar.issues && selectedCar.issues.length > recentIssues.length && (
                <Button
                  variant="link"
                  onClick={() => navigate(`/cars/${selectedCar.id}`)}
                  className="w-full text-center py-2 text-soft-blue font-medium"
                >
                  View All Issues
                </Button>
              )}
            </CardContent>
          </Card>
        )}
        
        {recentRepairs.length > 0 && (
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Recent Repairs</CardTitle>
              <Button 
                variant="link" 
                onClick={() => navigate(`/log-repair/${selectedCar.id}`)}
                className="text-soft-blue"
              >
                <Plus size={16} className="mr-1" />
                Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentRepairs.map(repair => (
                <div 
                  key={repair.id}
                  onClick={() => navigate(`/log-repair/${selectedCar.id}/${repair.id}`)}
                  className="p-3 border border-gray-100 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-750 cursor-pointer"
                >
                  <div className="flex items-start">
                    <Wrench size={20} className="text-soft-blue" />
                    <div className="ml-3 flex-1">
                      <h3 className="font-medium">{repair.title}</h3>
                      {repair.description && (
                        <p className="text-sm text-gray-500 line-clamp-1">{repair.description}</p>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <span className="text-xs text-gray-400">
                          {format(new Date(repair.date), 'PP')}
                        </span>
                        {repair.cost && (
                          <Badge variant="outline" className="bg-gray-50">
                            ${repair.cost.toFixed(2)}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              
              {selectedCar.repairs && selectedCar.repairs.length > recentRepairs.length && (
                <Button
                  variant="link"
                  onClick={() => navigate('/reports')}
                  className="w-full text-center py-2 text-soft-blue font-medium"
                >
                  View All Repairs
                </Button>
              )}
            </CardContent>
          </Card>
        )}
        
        <div className="pt-4 text-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="text-red-500 border-red-200">
                <Trash2 size={16} className="mr-2" />
                Delete Car
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem className="text-red-500" onClick={handleDelete}>
                Confirm Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </PageLayout>
  );
};

export default CarViewPage;
